/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useAsync } from 'react-streaming';
import xxhash from 'xxhash-wasm';

import type { WithFormErrors } from '@/components/Forms/FormControl';
import { LoginForm } from '@/pages/signin/+Page';
import { request } from '@/renderer/utils/api';
import type { RequestError } from '@/renderer/utils/errors';
import { ExpiredSessionError } from '@/renderer/utils/errors';
import { usePageContext } from '@/renderer/utils/usePageContext';
import type { JSONValue } from '@/types/json';
import type { Method, RequestSchema } from '@/types/request';

import { IS_SERVER } from './ssr';

export default useRequest;

export interface UseRequestOpts<T, Q> {
  manual?: boolean;
  clientOnly?: boolean;
  // @todo somehow cast this better
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatResult?: (data: any) => T;
  method?: Method;
  query?: Q;
  hideAlert?: boolean;
  skipRefresh?: boolean;
  storeRaw?: boolean;
}

interface UseRequestRunFnOpts<RE extends boolean, Raw extends boolean> {
  throwOnRun?: boolean;
  returnError?: RE;
  rawResult?: Raw;
  dontStoreError?: boolean;
  // @todo AbortController can be reinitialized in run(); we should be able to get the whole promise + new abort
  wrapped?: boolean;
}

type UseRequestRunFnResult<T, PassedRE extends boolean, PassedRaw extends boolean> = Promise<
  ExtractRaw<PassedRaw, T> | null | (PassedRE extends true ? Error : null)
>;

type UseRequestRunFn<T, D, _E = unknown | RequestError | Error> = <
  const PassedRE extends boolean = false,
  const PassedRaw extends boolean = false,
>(
  props?: D,
  opts?: UseRequestRunFnOpts<PassedRE, PassedRaw>,
) => UseRequestRunFnResult<T, PassedRE, PassedRaw>;

type ExtractRaw<Raw extends boolean, T> = Raw extends true
  ? T
  : T extends { data: infer B }
    ? B
    : T;

function extractResult<const T, const R = T extends { data: infer B } ? B : T>(
  obj: T,
  raw: boolean | undefined,
) {
  if (raw) {
    return obj as T;
  }

  return ((obj as { data: any })?.data || obj) as R;
}

export interface UseRequestResult<T, D, E = unknown | RequestError | Error> {
  data: T | undefined;
  error?: E;
  loading: boolean;
  setError: React.Dispatch<React.SetStateAction<Error | null>>;
  run: UseRequestRunFn<T, D, E>;
  abort: AbortController['abort'];
  // LoginForm?: typeof LoginForm;
}

export interface SubFormProps<V extends FieldValues> {
  form: UseFormReturn<WithFormErrors<V>>;
  onSubmit: (data?: V | any) => Promise<void> | void;
  request: UseRequestResult<any, V>;
}

/** Errors from the server need to be serialized into a plain object first */
function errorToObject(item: Error) {
  const obj: Record<any, any> = {};

  const descriptors = Object.getOwnPropertyDescriptors(item);

  for (const key in descriptors) {
    obj[key] = descriptors[key];
  }

  obj.__constructor__ = item.constructor?.name || 'Error';

  return obj;
}

/** When loading cached errors on the client, deserialize them back */
function errorFromObject(item: Record<any, any>) {
  const { __constructor__, ...props } = item;
  const str = __constructor__;
  let proto = Error.prototype;

  if (typeof str === 'string' && /^[a-zA-Z0-9_]+$/.test(str)) {
    try {
      const constructor = eval(str);

      if (constructor && constructor.prototype instanceof Error) {
        proto = constructor.prototype;
      }
    } catch (error) {
      console.warn(`Constructor "${str}" out of bounds, falling back to Error`);
    }
  }

  return Object.create(proto, props);
}

function genName<const T, const D = undefined | void>(req: RequestSchema<T, D>) {
  return req.input.toString();
}

/** In SSR, we use useAsync, which throws the promise until it is finished */
const useAsyncOrEffect: <T>(a: Parameters<typeof useEffect>[1], b: () => T) => Awaited<T> | void =
  IS_SERVER
    ? useAsync
    : (a: Parameters<typeof useEffect>[1], b: Parameters<typeof useAsync>[1]) => {
        useEffect(() => {
          b();
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, a);
      };

function noopFormat<T>(data: T) {
  return data;
}

export function useRequest<const T, const D = undefined | void, const Q = JSONValue>(
  reqo: RequestSchema<T, D> | string,
  opts?: UseRequestOpts<T, Q>,
) {
  const req: RequestSchema<T, D> = useMemo(() => {
    return typeof reqo === 'string' ? { input: reqo } : reqo;
  }, [reqo]);
  const name = req.name ? req.name : genName(req);
  const pageContext = usePageContext();
  const formatResult = opts?.formatResult || noopFormat;

  const requests: typeof pageContext.requests = useMemo(() => {
    return pageContext.requests || {};
  }, [pageContext.requests]);

  // Hash the body, if any
  const rkeyHasher = useAsync([req.init?.body, req.init?.headers], async () => {
    if (req.init) {
      const str = JSON.stringify(req.init);

      const { h64 } = await xxhash();

      return await h64(str);
    }
  });

  // Create a cache key for the server when we hydrate the client
  const rkey = `${req.method || 'GET'}:${name}${rkeyHasher ? ':' + rkeyHasher : ''}`;

  const alreadyHaveData = !!requests[rkey];
  const runAutomatically = !opts?.manual;
  const onlyRenderOnClient = !!opts?.clientOnly;

  const count = useRef(0);
  const [loading, setLoading] = useState(
    !alreadyHaveData && runAutomatically && (onlyRenderOnClient || !IS_SERVER) ? true : false,
  );

  if (!IS_SERVER && requests[rkey] && requests[rkey].__constructor__) {
    // Convert this error back once
    requests[rkey] = errorFromObject(requests[rkey]);
  }

  const [error, setError] = useState<Error | null>(
    requests[rkey] && requests[rkey] instanceof Error ? requests[rkey] : undefined,
  );

  const [data, setData] = useState<T>(
    requests[rkey] && !(requests[rkey] instanceof Error) ? requests[rkey] : undefined,
  );

  const [abortController, setAbortController] = useState<AbortController>(new AbortController());

  const run = useCallback(
    async <const RE extends boolean = false, const Raw extends boolean = false>(
      props?: D,
      { throwOnRun, rawResult, returnError, dontStoreError }: UseRequestRunFnOpts<RE, Raw> = {},
    ): UseRequestRunFnResult<T, RE, Raw> => {
      try {
        setError(null);
        setLoading(true);

        // @todo implement `query` parameter

        const strProps = props ? JSON.stringify(props) : undefined;
        let hkey = rkey;

        if (strProps) {
          const { h64 } = await xxhash();

          hkey += ':' + (await h64(strProps));
        }

        let nextAbortController = abortController;

        if (abortController.signal.aborted) {
          nextAbortController = new AbortController();
          setAbortController(nextAbortController);
        }

        const json: T = await formatResult(
          await request(
            {
              id: hkey,
              ...req,
              init: {
                ...req.init,
                signal: req.init?.signal || nextAbortController.signal,
              },
            },
            {
              body: strProps,
              noStringify: true,
              query: opts?.query,
              hideAlert: opts?.hideAlert,
              skipRefresh: opts?.skipRefresh,
              fetch: IS_SERVER ? pageContext.fetch : undefined, // SSR fetch includes headers from page request
            },
          ),
        );

        setData(extractResult(json, opts?.storeRaw));
        setLoading(false);

        if (IS_SERVER) {
          requests[hkey] = extractResult(opts?.storeRaw ? { ...json } : json, opts?.storeRaw);

          if (pageContext.requests) {
            pageContext.requests = { ...pageContext.requests, ...requests };
          } else {
            pageContext.requests = requests;
          }
        }

        return extractResult(json, rawResult) as ExtractRaw<Raw, T>;
      } catch (err) {
        if (!dontStoreError) {
          if (err instanceof Error) {
            setError(err);

            if (IS_SERVER) {
              requests[rkey] = errorToObject(err);

              if (pageContext.requests) {
                pageContext.requests = { ...pageContext.requests, ...requests };
              } else {
                pageContext.requests = requests;
              }
            }
          }
        }

        // console.warn(err);

        setLoading(false);

        if (throwOnRun) {
          throw err;
        }

        return (returnError ? err : null) as RE extends true ? Error : null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      abortController,
      formatResult,
      opts?.hideAlert,
      opts?.query,
      opts?.skipRefresh,
      pageContext,
      req,
      requests,
      rkey,
    ],
  );

  // console.log('what changed', [req.name, req.input, req.init?.body, opts?.query]);

  const asyncResult = useAsyncOrEffect(
    [req.name, req.input, req.init?.body, opts?.query],
    async () => {
      if (requests[rkey]) {
        // Hydrate the client using the data from the server that was already fetched and pushed into context cache
        const result: T = await formatResult(requests[rkey]);
        const err = requests[rkey] instanceof Error ? (requests[rkey] as Error) : undefined;

        if (!IS_SERVER) {
          // This is no longer needed in the cache; we've hydrated the client
          // delete requests[rkey];
        }

        if (err) {
          setError(err);
        } else {
          setError(null);
          setData(result);
        }

        setLoading(false);

        return Object.create(Object.prototype, {
          data: { value: err ? null : result, enumerable: true },
          error: { value: err, enumerable: true },
          loading: { value: false, enumerable: true },
          LoginForm: { value: err instanceof ExpiredSessionError ? LoginForm : undefined }, // not enumerable to avoid SSR serialization issue
          run: { value: run }, // not enumerable to avoid SSR serialization issue
          abort: { value: abortController.abort }, // not enumerable to avoid SSR serialization issue
        }) as UseRequestResult<T, D, typeof err>;
      } else if (runAutomatically && (!onlyRenderOnClient || IS_SERVER)) {
        // Fetch this data for the first time (or because something changed)
        count.current++;

        const result = await run(undefined, {
          returnError: true,
          rawResult: opts?.storeRaw || false,
        });
        const err = result instanceof Error ? result : undefined;

        return Object.create(Object.prototype, {
          data: { value: err ? null : (result as T), enumerable: true },
          error: { value: err, enumerable: true },
          loading: { value: false, enumerable: true },
          LoginForm: { value: err instanceof ExpiredSessionError ? LoginForm : undefined }, // not enumerable to avoid SSR serialization issue
          setError: { value: setError }, // not enumerable to avoid SSR serialization issue
          run: { value: run }, // not enumerable to avoid SSR serialization issue
          abort: { value: abortController.abort }, // not enumerable to avoid SSR serialization issue
        }) as UseRequestResult<T, D, typeof err>;
      } else if (IS_SERVER && onlyRenderOnClient) {
        // console.log('not running on server', name);
      } else if (!runAutomatically) {
        // console.log('not running automatically', name);
      } else {
        // console.log('wtf?', name);
      }
    },
  );

  if (IS_SERVER && asyncResult) {
    return asyncResult;
  }

  return {
    data,
    error,
    loading,
    // LoginForm: error instanceof ExpiredSessionError ? LoginForm : undefined,
    setError,
    run,
    abort: abortController.abort,
  } satisfies UseRequestResult<T, D, typeof error>;
}
